import type {
  Conversation,
  Message,
  ChatResponse,
  ApiError,
  Project,
  ProjectListItem,
  CreateProjectRequest,
  UpdateProjectRequest,
  Artifact,
  ArtifactWithContent,
  CreateArtifactRequest,
  Deployment,
  DeployProjectRequest,
  Recipe,
  RecipeListItem,
  SearchResponse,
  SearchType,
} from '@/types';

const API_BASE = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api`
  : '/api';

class ApiClient {
  private token: string | null = null;

  setToken(token: string | null) {
    this.token = token;
  }

  private async request<T>(
    path: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error: ApiError = await response.json().catch(() => ({
        error: 'An error occurred',
        status: response.status,
      }));
      throw new Error(error.error);
    }

    return response.json();
  }

  // === Projects ===

  async listProjects(options?: {
    status?: string;
    include_archived?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<ProjectListItem[]> {
    const params = new URLSearchParams();
    if (options?.status) params.set('status', options.status);
    if (options?.include_archived) params.set('include_archived', 'true');
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());

    const query = params.toString();
    return this.request(`/projects${query ? `?${query}` : ''}`);
  }

  async createProject(data: CreateProjectRequest): Promise<Project> {
    return this.request('/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getProject(id: string): Promise<Project> {
    return this.request(`/projects/${id}`);
  }

  async updateProject(id: string, data: UpdateProjectRequest): Promise<Project> {
    return this.request(`/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteProject(id: string): Promise<void> {
    await this.request(`/projects/${id}`, {
      method: 'DELETE',
    });
  }

  async getProjectConversations(projectId: string): Promise<Conversation[]> {
    return this.request(`/projects/${projectId}/conversations`);
  }

  // === Artifacts ===

  async listArtifacts(projectId: string, options?: {
    file_type?: string;
    uncommitted_only?: boolean;
  }): Promise<Artifact[]> {
    const params = new URLSearchParams();
    if (options?.file_type) params.set('file_type', options.file_type);
    if (options?.uncommitted_only) params.set('uncommitted_only', 'true');

    const query = params.toString();
    return this.request(`/projects/${projectId}/artifacts${query ? `?${query}` : ''}`);
  }

  async createArtifact(projectId: string, data: CreateArtifactRequest): Promise<ArtifactWithContent> {
    return this.request(`/projects/${projectId}/artifacts`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getArtifact(projectId: string, artifactId: string): Promise<ArtifactWithContent> {
    return this.request(`/projects/${projectId}/artifacts/${artifactId}`);
  }

  async updateArtifact(
    projectId: string,
    artifactId: string,
    data: { name?: string; content?: string }
  ): Promise<ArtifactWithContent> {
    return this.request(`/projects/${projectId}/artifacts/${artifactId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteArtifact(projectId: string, artifactId: string): Promise<void> {
    await this.request(`/projects/${projectId}/artifacts/${artifactId}`, {
      method: 'DELETE',
    });
  }

  // === Deployments ===

  async listDeployments(projectId: string, options?: {
    environment?: string;
    status?: string;
  }): Promise<Deployment[]> {
    const params = new URLSearchParams();
    if (options?.environment) params.set('environment', options.environment);
    if (options?.status) params.set('status', options.status);

    const query = params.toString();
    return this.request(`/projects/${projectId}/deployments${query ? `?${query}` : ''}`);
  }

  async deployProject(projectId: string, data?: DeployProjectRequest): Promise<Deployment> {
    return this.request(`/projects/${projectId}/deploy`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }

  // === Recipes ===

  async listRecipes(options?: {
    category?: string;
    include_private?: boolean;
  }): Promise<RecipeListItem[]> {
    const params = new URLSearchParams();
    if (options?.category) params.set('category', options.category);
    if (options?.include_private) params.set('include_private', 'true');

    const query = params.toString();
    return this.request(`/recipes${query ? `?${query}` : ''}`);
  }

  async getRecipe(idOrSlug: string): Promise<Recipe> {
    return this.request(`/recipes/${idOrSlug}`);
  }

  async listRecipeCategories(): Promise<string[]> {
    return this.request('/recipes/categories');
  }

  // === Search ===

  async search(query: string, options?: {
    type?: SearchType;
    project_id?: string;
    limit?: number;
    offset?: number;
  }): Promise<SearchResponse> {
    const params = new URLSearchParams();
    params.set('q', query);
    if (options?.type) params.set('type', options.type);
    if (options?.project_id) params.set('project_id', options.project_id);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());

    return this.request(`/search?${params.toString()}`);
  }

  async searchProject(projectId: string, query: string): Promise<SearchResponse> {
    const params = new URLSearchParams();
    params.set('q', query);
    return this.request(`/projects/${projectId}/search?${params.toString()}`);
  }

  // === Conversations ===

  async listConversations(options?: {
    favorites_only?: boolean;
    include_archived?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<Conversation[]> {
    const params = new URLSearchParams();
    if (options?.favorites_only) params.set('favorites_only', 'true');
    if (options?.include_archived) params.set('include_archived', 'true');
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());

    const query = params.toString();
    return this.request(`/conversations${query ? `?${query}` : ''}`);
  }

  async createConversation(title?: string, projectId?: string): Promise<Conversation> {
    return this.request('/conversations', {
      method: 'POST',
      body: JSON.stringify({ title, project_id: projectId }),
    });
  }

  async getConversation(id: string): Promise<Conversation> {
    return this.request(`/conversations/${id}`);
  }

  async updateConversation(
    id: string,
    data: { title?: string; is_favorite?: boolean; is_archived?: boolean }
  ): Promise<Conversation> {
    return this.request(`/conversations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteConversation(id: string): Promise<void> {
    await this.request(`/conversations/${id}`, {
      method: 'DELETE',
    });
  }

  // === Messages ===

  async listMessages(
    conversationId: string,
    options?: { limit?: number; before?: string }
  ): Promise<Message[]> {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.before) params.set('before', options.before);

    const query = params.toString();
    return this.request(
      `/conversations/${conversationId}/messages${query ? `?${query}` : ''}`
    );
  }

  async sendMessage(
    conversationId: string,
    content: string,
    model?: string
  ): Promise<ChatResponse> {
    return this.request(`/conversations/${conversationId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ content, model }),
    });
  }

  // === Health ===

  async healthCheck(): Promise<{ status: string; service: string }> {
    return this.request('/health');
  }
}

export const api = new ApiClient();
